import React from "react";
import { withRouter } from "react-router";

import { Button, Checkbox, FormControlLabel, Box, TextField, Typography, CircularProgress, Snackbar } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import { Nav } from "../../components/Interface";
import { FormFieldWrapper } from '../../components/Global';
import api from "../../utils/api";
import { MuiRouterLink, WarningButton } from "../../components/CustomMui";

const emailRegex = /^w+[+.w-]*@([w-]+.)*w+[w-]*.([a-z]{2,4}|d+)$/i

const defaultSnackbar = {
  message: '',
  open: false,
  severity: 'success'
}

const defaultState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  isActive: true,
  isAdmin: false,
  errors: {},
  snackbar: defaultSnackbar
}

export class UserWithoutRouter extends React.Component { 
  state = {
    ...defaultState,
    id: this.props.match.params.userId ? this.props.match.params.userId : null,
    spinner: this.props.match.params.userId ? true : false,
  }

  componentDidMount() {
    const { userId } = this.props.match.params;
    
    if (userId) {
      document.title = 'Edit User | PAVILION Interactive Builder';
      api.getUser(userId)
        .then(res => {
          this.setState(res.data)
        })
        .catch(err => {
          this.setState({notFound:true})
        })
        .finally(() => {
          this.setState({spinner: false})
        })
    } else {
      document.title = 'Create User | PAVILION Interactive Builder';
    }
  }

  isEditForm = () => this.props.match.params.userId ? true : false;

  validateString = (str,name, errors) => {

    if (typeof str !== 'string') {
      errors[name] = "Please enter a value"
    } else if (!str.trim()) {
      errors[name] = "Value must be alphanumeric"
    }

    return errors;
  }

  validateBool = (bool, name, errors) => {

    if (typeof bool !== 'boolean') {
      errors[name] = "Value must true or false"
    }

    return errors;
  }

  validatePassword = (pw, name, errors) => {
    if (typeof pw !== 'string') {
      errors[name] = "Password must have at least 3 alphanumeric characters"
    } else if (pw.length < 6) {
      errors[name] = "Password must have 6 characters"
    } else if ( !pw.match(/[A-Z]/) ) {
      errors[name] = "Password must have at least one uppercase character"
    } else if ( !pw.match(/[a-z]/) ) {
      errors[name] = "Password must have at least one lowercase character"
    } else if ( !pw.match(/[0-9]/) ) {
      errors[name] = "Password must have at least one numeric character"
    }

    return errors;
  }

  validateEmail = (email, name, errors) => {

    if (typeof email !== 'string') {
      errors[name] = "Value must be alphanumeric"
    } else if (!email.trim()) {
      errors[name] = "Please enter a value"
    } else if (!email.match(emailRegex)) {
      errors[name] = "Please enter a valid email"
    }
    return errors;
  }

  validateUser = user => {
    const {firstName, lastName, email, password, isAdmin, isActive} = user;
    let errors = {};
    
    errors = this.validateString(firstName, 'firstName', errors)
    errors = this.validateString(lastName, 'lastName', errors)
    errors = this.validateString(email, 'email', errors)

    // always validate password for create form 
    // validate for edit form when password field is not empty
    if (password || !this.isEditForm()) {
      errors = this.validatePassword(password, 'password', errors)
    }

    errors = this.validateBool(isAdmin, 'isAdmin', errors)
    errors = this.validateBool(isActive, 'isActive', errors)

    this.setState({errors})

    if (Object.keys(errors).length === 0) {
      return true;
    } else {
      return false;
    }
  }

  createUser = user => {
    api.createUser(user)
    .then(res => {
      this.setState({
        ...defaultState,
        snackbar: {
          open: true,
          message: `User "${user.firstName} ${user.lastName}" successfully created.`,
          severity: 'success'
        }
      }, () => {
        window.document.documentElement.scrollTo({
          top: 0,
          behavior: "smooth"
        })
      })
    })
    .catch(err => {
      if (err.response) {
        console.log(err.response)
      } else {
        console.log(err)
      }
      this.setState({
        snackbar: {
          open: true,
          message: `An error occurred. This user was not created.`,
          severity: 'error'
        }
      })
    })
  }

  updateUser = user => (
    api.updateUser(user)
    .then(res => {
      this.setState({
        snackbar: {
          open: true,
          message: `User "${user.firstName} ${user.lastName}" successfully updated.`,
          severity: 'success'
        }
      })
    })
    .catch(err => {
      if (err.response) {
        console.log(err.response)
      } else {
        console.log(err)
      }
      this.setState({
        snackbar: {
          open: true,
          message: `An error occurred. Changes to this user were not saved.`,
          severity: 'error'
        }
      })
    })
  )

  handleSubmit = (e) => {
    e.preventDefault();

    let user = {...this.state}
    delete user.spinner;
    delete user.notFound;
    delete user.errors;
    delete user.message;
    
    const userIsValid = this.validateUser(user);

    if (!userIsValid) {
      return;
    }

    if (!user.password) {
      delete user.password;
    }

    if (this.isEditForm()) {
     this.updateUser(user);
    } else {
      this.createUser(user);
    }

  }

  handleDelete = () => {
    api.deleteUser(this.state.id)
    .then(res => {
      this.props.history.push('/users')
    })
    .catch(err => {
      if (err.response) {
        console.log(err.response)
      } else {
        console.log(err)
      }
      this.setState({
        snackbar: {
          open: true,
          message: `An error occurred. This user was not deleted.`,
          severity: 'error'
        }
      })
    })
  }

  handleTextFieldChange = (e) => {
    const {name, value} = e.target;
    this.setState({
      [name]:value
    })
  }

  handleCheckboxChange = (e) => {
    const {name} = e.target;
    this.setState({
      [name]: !this.state[name]
    })
  }

  handleSnackbarClose = (e) => {
    this.setState({snackbar: {...defaultSnackbar}})
  }

  render() {
   
    return (
      <>
        <Nav />

        <Box padding={3}>

          <Typography component={MuiRouterLink} to="/users"><i className="fas fa-arrow-left"></i>{' All Users'}</Typography>

          {this.isEditForm()
            ? <h1>Edit User</h1>
            : <h1>Add User</h1>
          }
          
          { this.state.spinner 
          ? <CircularProgress size={70} /> 
          : this.state.notFound
            ? <Typography>User not found</Typography>
            :
              <form onSubmit={this.handleSubmit}>
                <FormFieldWrapper>
                  <TextField
                    id="first"
                    label="First Name" 
                    name="firstName"
                    value={this.state.firstName}
                    onChange={this.handleTextFieldChange}
                    error={this.state.errors.firstName}
                    helperText={this.state.errors.firstName}
                  />
                </FormFieldWrapper>

                <FormFieldWrapper>
                  <TextField
                    id="last"
                    label="Last Name" 
                    name="lastName"
                    value={this.state.lastName}
                    onChange={this.handleTextFieldChange}
                    error={this.state.errors.lastName}
                    helperText={this.state.errors.lastName}
                  />
                </FormFieldWrapper>

                <FormFieldWrapper>
                  <TextField
                    id="email"
                    label="Email"
                    name="email"
                    type="email"
                    value={this.state.email}
                    onChange={this.handleTextFieldChange}
                    helperText={ this.state.errors.email || "Email will be used as username"}
                    error={this.state.errors.email}
                  />
                </FormFieldWrapper>

                <FormFieldWrapper>
                  <TextField
                    id="password"
                    label="Password" 
                    name="password"
                    value={this.state.password}
                    onChange={this.handleTextFieldChange}
                    helperText={ "Password must have an uppercase character, a lowercase character, a digit, and a non-alphanumeric character. Password must be at least six characters long." }
                    error={this.state.errors.password}
                  />
                </FormFieldWrapper>

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={this.state.isActive}
                      onChange={this.handleCheckboxChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="Is active user?"
                  error={this.state.errors.isActive}
                />
                <br />

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={this.state.isAdmin}
                      onChange={this.handleCheckboxChange}
                      name="isAdmin"
                      color="primary"
                    />
                  }
                  label="Is Admin?"
                  error={this.state.errors.isAdmin}
                />
                <br />

                <Button 
                  color="primary"
                  type="submit"
                >
                  Save User
                </Button>
              
              {this.isEditForm() 
                ? <WarningButton 
                    type="button" 
                    style={{marginLeft: 15, fontSize: 15}} 
                    onClick={this.handleDelete}
                  >
                    Delete User
                  </WarningButton>
                : ''
              }

              </form>
          }

          <Snackbar open={this.state.snackbar.open} autoHideDuration={3500} onClose={this.handleSnackbarClose}>
            <Alert onClose={this.handleSnackbarClose} severity={this.state.snackbar.severity}>
              {this.state.snackbar.message}
            </Alert>
          </Snackbar>

        </Box>
      </>
    );
  }
}

export const User = withRouter(UserWithoutRouter);